import { useLayoutEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

interface BentoItem {
  num: string;
  title: string;
  short: string;
  long: string;
  image: string;
  alt: string;
}

const ITEMS: BentoItem[] = [
  {
    num: '01',
    title: 'Dive',
    short: 'Descend into protected coral gardens.',
    long:
      'Guided descents into the marine reserves off Fajardo — hawksbill turtles, elkhorn reefs, and shafts of sunlight breaking against the sand thirty meters below.',
    image:
      'https://readdy.ai/api/search-image?query=Ultra-realistic%20editorial%20underwater%20photograph%20of%20a%20protected%20Caribbean%20coral%20reef%2C%20turquoise%20blue%20water%2C%20delicate%20elkhorn%20and%20brain%20coral%2C%20schools%20of%20small%20silver%20fish%2C%20cathedral-like%20shafts%20of%20golden%20sunlight%20penetrating%20from%20the%20surface%2C%20clear%20visibility%2C%20cinematic%20contrast%2C%20luxury%20travel%20magazine%20aesthetic%2C%20soft%20natural%20colors&width=1600&height=1200&seq=bento-dive-underwater-01&orientation=landscape',
    alt: 'Underwater coral reef with sunlight rays',
  },
  {
    num: '02',
    title: 'Sail',
    short: 'A chartered catamaran across the trade winds.',
    long:
      'A private crew, a hand-built catamaran, and a slow arc around the outer keys. Champagne at anchor. Sunset marked only by the shift of light on the sails.',
    image:
      'https://readdy.ai/api/search-image?query=Ultra-realistic%20aerial%20drone%20photograph%20of%20a%20luxury%20white%20sailing%20catamaran%20anchored%20alone%20in%20clear%20turquoise%20Caribbean%20water%2C%20visible%20sand%20patterns%20beneath%2C%20soft%20afternoon%20light%2C%20subtle%20wake%2C%20editorial%20yacht%20magazine%20photography%2C%20high%20detail%2C%20muted%20cinematic%20tones%2C%20minimal%20composition&width=1600&height=1200&seq=bento-sail-catamaran-02&orientation=landscape',
    alt: 'Luxury catamaran anchored in turquoise water',
  },
  {
    num: '03',
    title: 'Tee',
    short: 'Seaside fairways with the sound of surf.',
    long:
      'A private tee time on the trade-wind course carved along the Atlantic shelf. Emerald fairways, dark volcanic sand traps, and the sea always in view.',
    image:
      'https://readdy.ai/api/search-image?query=Ultra-realistic%20editorial%20photograph%20of%20a%20dramatic%20oceanfront%20golf%20course%20fairway%20on%20the%20Puerto%20Rican%20coast%2C%20lush%20green%20grass%2C%20long%20palm%20tree%20shadows%20cast%20across%20the%20green%2C%20Atlantic%20ocean%20visible%20in%20background%2C%20soft%20late%20afternoon%20light%2C%20luxury%20travel%20magazine%20aesthetic%2C%20minimal%20composition&width=1200&height=1200&seq=bento-tee-golf-03&orientation=squarish',
    alt: 'Dramatic oceanfront golf course with palm shadows',
  },
  {
    num: '04',
    title: 'Stay',
    short: 'Cliffside villas where architecture meets horizon.',
    long:
      'Six residences etched into the ridge. Board-formed concrete, floor-to-ceiling glass, and infinity pools that dissolve into the Atlantic. No two identical.',
    image:
      'https://readdy.ai/api/search-image?query=Ultra-realistic%20architectural%20photograph%20of%20a%20minimalist%20luxury%20infinity%20pool%20villa%2C%20board-formed%20concrete%20architecture%2C%20lush%20tropical%20jungle%20surrounding%2C%20cliffside%20setting%20with%20ocean%20horizon%2C%20soft%20warm%20afternoon%20light%2C%20clean%20geometry%2C%20editorial%20architectural%20digest%20aesthetic%2C%20muted%20natural%20tones&width=1200&height=1200&seq=bento-stay-villa-04&orientation=squarish',
    alt: 'Minimalist cliffside infinity pool with ocean view',
  },
  {
    num: '05',
    title: 'Dine',
    short: 'Open-air tasting menus under the stars.',
    long:
      'A single long table set at the tide line. A rotating chef in residence, native produce, and a menu written the morning of. Only ever twelve guests.',
    image:
      'https://readdy.ai/api/search-image?query=Ultra-realistic%20editorial%20photograph%20of%20a%20candlelit%20beach%20dinner%20at%20dusk%2C%20long%20wooden%20table%20set%20on%20pale%20sand%20near%20the%20tide%20line%2C%20contemporary%20ceramic%20tableware%2C%20wildflowers%2C%20soft%20warm%20candlelight%2C%20ocean%20in%20background%2C%20luxury%20travel%20magazine%20aesthetic%2C%20cinematic%20color%20grading%2C%20minimalism&width=1200&height=1200&seq=bento-dine-beach-05&orientation=squarish',
    alt: 'Candlelit beach dinner at dusk',
  },
];

export default function BentoGrid() {
  const rootRef = useRef<HTMLElement>(null);
  const [flippedIndex, setFlippedIndex] = useState<number | null>(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const isNarrow = window.innerWidth <= 760;

      const cards = gsap.utils.toArray<HTMLElement>('.bento__card');
      if (!cards.length) return;

      if (prefersReduced || isNarrow) {
        gsap.set(cards, { opacity: 1, y: 0, rotationX: 0, z: 0 });
        return;
      }

      gsap.set(cards, {
        opacity: 0,
        y: 60,
        z: -500,
        rotationX: 25,
        transformOrigin: 'center center',
      });

      gsap.to(cards, {
        opacity: 1,
        y: 0,
        z: 0,
        rotationX: 0,
        stagger: 0.06,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: rootRef.current,
          start: 'top 80%',
          end: 'top 20%',
          scrub: true,
        },
      });
    }, rootRef);
    return () => ctx.revert();
  }, []);

  const handleCardClick = (index: number) => {
    setFlippedIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section
      ref={rootRef}
      className="bento"
      id="experiences"
      aria-label="Curated experiences"
    >
      <div className="bento__inner">
        {ITEMS.map((item, i) => {
          const isFlipped = flippedIndex === i;
          const isDim = flippedIndex !== null && flippedIndex !== i;
          return (
            <button
              key={item.num}
              type="button"
              onClick={() => handleCardClick(i)}
              className={[
                'bento__card',
                `bento__card--${i + 1}`,
                isFlipped ? 'is-flipped' : '',
                isDim ? 'is-dim' : '',
              ].join(' ')}
              data-cursor="expand"
              aria-label={`Experience ${item.title}`}
              aria-pressed={isFlipped}
            >
              <div className="bento__face bento__face--front">
                <img
                  className="bento__img"
                  src={item.image}
                  alt={item.alt}
                />
                <div className="bento__overlay" />
                <div className="bento__content">
                  <div className="bento__num">{item.num}</div>
                  <div className="bento__title">{item.title}</div>
                  <div className="bento__desc">{item.short}</div>
                </div>
              </div>
              <div className="bento__face bento__face--back">
                <div className="bento__num" style={{ color: 'var(--accent)' }}>
                  {item.num} — {item.title}
                </div>
                <div>
                  <div className="bento__back-title">{item.title}</div>
                  <p className="bento__back-body" style={{ marginTop: '1.5rem' }}>
                    {item.long}
                  </p>
                </div>
                <span className="bento__back-close">← Close</span>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}